import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ExternalLink, Users, RefreshCw, LogOut, ChevronDown, ChevronUp, Share2, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Discord brand color
const DISCORD_PURPLE = '#5865F2';

const DiscordWidget = ({ user, onShareStats }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [discordUser, setDiscordUser] = useState(null);
  const [guilds, setGuilds] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [beatseekServer, setBeatseekServer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllGuilds, setShowAllGuilds] = useState(false);
  const [showCommunities, setShowCommunities] = useState(false);

  // Check for existing Discord connection on mount
  useEffect(() => {
    const token = localStorage.getItem('discord_access_token');
    if (token) {
      validateAndFetchData(token);
    }
    fetchCommunities();
  }, []);

  // Listen for OAuth popup messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'discord_auth_success') {
        localStorage.setItem('discord_access_token', event.data.token);
        if (event.data.refresh_token) {
          localStorage.setItem('discord_refresh_token', event.data.refresh_token);
        }
        setDiscordUser(event.data.user);
        setIsConnected(true);
        window.dispatchEvent(new Event('beatseek:connections-changed'));
        fetchGuilds(event.data.token);
      } else if (event.data.type === 'discord_auth_error') {
        setError(event.data.error || 'Authentication failed');
        setLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const validateAndFetchData = async (token) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/discord/user?access_token=${encodeURIComponent(token)}`);
      
      if (response.ok) {
        const userData = await response.json();
        setDiscordUser(userData);
        setIsConnected(true);
        await fetchGuilds(token);
      } else {
        // Token expired or invalid
        localStorage.removeItem('discord_access_token');
        localStorage.removeItem('discord_refresh_token');
        setIsConnected(false);
      }
    } catch (err) {
      console.error('Discord validation error:', err);
      localStorage.removeItem('discord_access_token');
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchGuilds = async (token) => {
    try {
      const response = await fetch(`${API}/discord/guilds?access_token=${encodeURIComponent(token)}`);
      if (response.ok) {
        const data = await response.json();
        setGuilds(data.guilds || []);
      }
    } catch (err) {
      console.error('Failed to fetch guilds:', err);
    }
  };

  const fetchCommunities = async () => {
    try {
      const response = await fetch(`${API}/discord/communities`);
      if (response.ok) {
        const data = await response.json();
        setCommunities(data.communities || []);
        setBeatseekServer(data.beatseek_server);
      }
    } catch (err) {
      console.error('Failed to fetch communities:', err);
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API}/discord/auth/url`);
      if (!response.ok) throw new Error('Failed to get auth URL');
      
      const data = await response.json();
      
      // Open OAuth popup
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const isMobile = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) || /Mobi/i.test(navigator.userAgent || '');
      if (isMobile) {
        // Mobile: full-page redirect — popups & window.opener are unreliable on phones
        window.location.assign(data.auth_url);
        return;
      }
      const popup = window.open(
        data.auth_url,
        'discord_auth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );
      // Popup blocked → fall back to same-tab redirect
      if (!popup) {
        window.location.assign(data.auth_url);
        return;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('discord_access_token');
    localStorage.removeItem('discord_refresh_token');
    setIsConnected(false);
    setDiscordUser(null);
    setGuilds([]);
    window.dispatchEvent(new Event('beatseek:connections-changed'));
  };

  const handleRefresh = async () => {
    const token = localStorage.getItem('discord_access_token');
    if (token) {
      setLoading(true);
      await validateAndFetchData(token);
      setLoading(false);
    }
  };

  const displayedGuilds = showAllGuilds ? guilds : guilds.slice(0, 4);

  return (
    <Card className="discord-widget-card" style={{
      background: 'linear-gradient(135deg, rgba(88, 101, 242, 0.1) 0%, rgba(30, 30, 50, 0.9) 100%)',
      border: `1px solid ${DISCORD_PURPLE}40`,
      marginTop: '20px'
    }}>
      <CardHeader style={{ borderBottom: `1px solid ${DISCORD_PURPLE}30`, padding: '12px 16px' }}>
        <CardTitle className="discord-section-title" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#fff'
        }}>
          <svg width="24" height="24" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z" fill="#5865F2"/>
          </svg>
          <span>Discord Integration</span>
          <span style={{
            fontSize: '0.65rem',
            color: 'rgba(88, 101, 242, 0.9)',
            background: 'rgba(88, 101, 242, 0.15)',
            padding: '0.2rem 0.5rem',
            borderRadius: '10px',
            marginLeft: 'auto',
            fontWeight: '500'
          }}>Powered by Discord</span>
        </CardTitle>
      </CardHeader>
      <CardContent style={{ padding: '12px' }}>
        {error && (
          <div style={{
            background: 'rgba(237, 66, 69, 0.1)',
            border: '1px solid rgba(237, 66, 69, 0.3)',
            borderRadius: '8px',
            padding: '10px 15px',
            marginBottom: '10px',
            color: '#ED4245',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {!isConnected ? (
          // Not Connected State
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <p style={{ color: '#a0a0a0', marginBottom: '12px', fontSize: '14px' }}>
              Connect your Discord account to see your servers and discover DJ communities!
            </p>
            <Button
              onClick={handleConnect}
              disabled={loading}
              data-testid="discord-connect-btn"
              style={{
                background: DISCORD_PURPLE,
                color: '#fff',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '0 auto',
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 71 55" fill="currentColor">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
              </svg>
              {loading ? 'Connecting...' : 'Connect with Discord'}
            </Button>
          </div>
        ) : (
          // Connected State
          <div>
            {/* User Profile Section */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              padding: '15px',
              background: 'rgba(88, 101, 242, 0.15)',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                overflow: 'hidden',
                border: `2px solid ${DISCORD_PURPLE}`
              }}>
                {discordUser?.avatar_url ? (
                  <img 
                    src={discordUser.avatar_url} 
                    alt={discordUser.username}
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: DISCORD_PURPLE,
                  display: discordUser?.avatar_url ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  {discordUser?.username?.charAt(0).toUpperCase() || 'D'}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#fff', fontWeight: '600', fontSize: '16px' }}>
                  {discordUser?.global_name || discordUser?.username}
                </div>
                <div style={{ color: '#a0a0a0', fontSize: '13px' }}>
                  @{discordUser?.username}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  onClick={handleRefresh}
                  disabled={loading}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${DISCORD_PURPLE}50`,
                    color: '#a0a0a0',
                    padding: '8px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  title="Refresh"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </Button>
                <Button
                  onClick={handleDisconnect}
                  style={{
                    background: 'rgba(237, 66, 69, 0.2)',
                    border: '1px solid rgba(237, 66, 69, 0.3)',
                    color: '#ED4245',
                    padding: '8px',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  title="Disconnect"
                >
                  <LogOut size={16} />
                </Button>
              </div>
            </div>

            {/* Your Servers Section */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <h4 style={{ color: '#fff', fontSize: '14px', fontWeight: '600', margin: 0 }}>
                  <Users size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                  Your Servers ({guilds.length})
                </h4>
              </div>
              
              {guilds.length === 0 ? (
                <p style={{ color: '#666', fontSize: '13px', textAlign: 'center', padding: '15px' }}>
                  No servers found
                </p>
              ) : (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: '10px'
                  }}>
                    {displayedGuilds.map((guild) => (
                      <a
                        key={guild.id}
                        href={`https://discord.com/channels/${guild.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px',
                          background: 'rgba(0, 0, 0, 0.3)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textDecoration: 'none'
                        }}
                        className="discord-guild-item"
                        title={`Open ${guild.name} in Discord`}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0,
                          background: '#36393f'
                        }}>
                          {guild.icon_url ? (
                            <img 
                              src={guild.icon_url} 
                              alt={guild.name}
                              crossOrigin="anonymous"
                              referrerPolicy="no-referrer"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: guild.icon_url ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {guild.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                        </div>
                        <span style={{
                          color: '#ddd',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1
                        }}>
                          {guild.name}
                        </span>
                        <ExternalLink size={12} style={{ color: '#666', flexShrink: 0 }} />
                      </a>
                    ))}
                  </div>
                  
                  {guilds.length > 4 && (
                    <Button
                      onClick={() => setShowAllGuilds(!showAllGuilds)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: DISCORD_PURPLE,
                        fontSize: '13px',
                        padding: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        margin: '10px auto 0'
                      }}
                    >
                      {showAllGuilds ? (
                        <>Show Less <ChevronUp size={16} /></>
                      ) : (
                        <>Show All ({guilds.length}) <ChevronDown size={16} /></>
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Discover DJ Communities */}
            <div>
              <Button
                onClick={() => setShowCommunities(!showCommunities)}
                style={{
                  width: '100%',
                  background: 'rgba(88, 101, 242, 0.1)',
                  border: `1px solid ${DISCORD_PURPLE}40`,
                  color: '#fff',
                  padding: '12px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: showCommunities ? '15px' : '0'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🎧</span>
                  <span>Discover DJ Communities</span>
                </span>
                {showCommunities ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </Button>

              {showCommunities && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* BEATSEEK Official Server - Featured */}
                  {beatseekServer && (
                    <a
                      href={beatseekServer.invite_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '15px',
                        background: `linear-gradient(135deg, ${DISCORD_PURPLE}30 0%, rgba(0,0,0,0.4) 100%)`,
                        border: `2px solid ${DISCORD_PURPLE}`,
                        borderRadius: '10px',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      className="discord-community-featured"
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${DISCORD_PURPLE}, #8B5CF6)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px'
                      }}>
                        🎵
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: '700', fontSize: '15px', marginBottom: '3px' }}>
                          {beatseekServer.name}
                          <span style={{
                            marginLeft: '8px',
                            background: DISCORD_PURPLE,
                            color: '#fff',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            OFFICIAL
                          </span>
                        </div>
                        <div style={{ color: '#a0a0a0', fontSize: '12px' }}>
                          {beatseekServer.description}
                        </div>
                      </div>
                      <ExternalLink size={18} style={{ color: '#a0a0a0' }} />
                    </a>
                  )}

                  {/* Other Communities */}
                  {communities.map((community) => (
                    <a
                      key={community.id}
                      href={community.invite_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      className="discord-community-item"
                    >
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#36393f',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px'
                      }}>
                        🎧
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>
                          {community.name}
                        </div>
                        <div style={{ color: '#666', fontSize: '11px' }}>
                          {community.member_count} members • {community.category}
                        </div>
                      </div>
                      <ExternalLink size={16} style={{ color: '#666' }} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Share Weekly Stats Button */}
            {onShareStats && (
              <div style={{ marginTop: '20px' }}>
                <Button
                  onClick={onShareStats}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, rgba(88, 101, 242, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                    border: `1px solid ${DISCORD_PURPLE}`,
                    color: '#fff',
                    padding: '14px 15px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <BarChart3 size={18} />
                  Share My Weekly Stats
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <style>{`
        .discord-guild-item:hover {
          background: rgba(88, 101, 242, 0.2) !important;
          transform: translateY(-2px);
        }
        .discord-community-item:hover,
        .discord-community-featured:hover {
          background: rgba(88, 101, 242, 0.2) !important;
          transform: translateX(5px);
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Card>
  );
};

export default DiscordWidget;
